import React, { useState, useRef } from "react";
import { Form, Modal, Button } from "react-bootstrap";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";

const OrganizationForm = ({
  organizationForm,
  setOrganizationForm,
  setOnFocus,
  error,
} = {}) => {
  // State for image cropping
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [imageError, setImageError] = useState(null);
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);

  const MAX_FILE_SIZE = 10 * 1024 * 1024;

  const handleLogoUpload = (e) => {
    // Reset previous errors
    setImageError(null);

    const file = e.target.files[0];
    if (file) {
      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        setImageError("File size should not exceed 10 MB");
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      // Validate file type
      const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
      if (!allowedTypes.includes(file.type)) {
        setImageError("Only PNG and JPEG images are allowed");
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
        setShowCropModal(true);
      };
      reader.readAsDataURL(file);
    }
  };

  // Function to apply initial crop
  function onImageLoad(e) {
    const { width, height } = e.currentTarget;
    const cropWidthInPercent = (100 / width) * Math.min(width, height);

    const crop = centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: cropWidthInPercent,
        },
        1, // Square aspect ratio (1:1)
        width,
        height
      ),
      width,
      height
    );

    setCrop(crop);
  }

  // Function to apply crop and save image
  const applyCrop = () => {
    if (!imgRef.current || !completedCrop) return;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    const scaleX = imgRef.current.naturalWidth / imgRef.current.width;
    const scaleY = imgRef.current.naturalHeight / imgRef.current.height;

    canvas.width = completedCrop.width;
    canvas.height = completedCrop.height;

    ctx.drawImage(
      imgRef.current,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      completedCrop.width,
      completedCrop.height
    );

    canvas.toBlob((blob) => {
      if (!blob) return;
      const previewUrl = URL.createObjectURL(blob);

      // Validate blob size again
      if (blob.size > MAX_FILE_SIZE) {
        setImageError("Cropped image exceeds 10 MB");
        return;
      }

      // Convert blob to file
      const croppedFile = new File([blob], "cropped-logo.png", {
        type: "image/png",
      });

      setOrganizationForm({
        ...organizationForm,
        logo: croppedFile,
        logoPreview: previewUrl,
      });

      setShowCropModal(false);
      setImageError(null);
    }, "image/png");
  };

  // Function to remove logo
  const handleRemoveLogo = () => {
    setOrganizationForm({
      ...organizationForm,
      logo: null,
      logoPreview: null,
    });

    // Reset file input if ref exists
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setImageError(null);
  };

  return (
    <>
      <div className="mb-4 d-flex flex-column align-items-center">
        {/* Logo Upload Container */}
        <div
          className="position-relative"
          style={{ width: "200px", height: "200px" }}
        >
          <div
            className="rounded-circle border-dashed hover-shadow"
            style={{
              width: "100%",
              height: "100%",
              cursor: "pointer",
              overflow: "hidden",
              border: "2px dotted #787C80FF",
              position: "relative",
              transition: "all 0.2s ease-in-out",
              boxShadow: organizationForm.logoPreview
                ? "0 4px 12px rgba(0,0,0,0.1)"
                : "none",
            }}
            onClick={() => document.getElementById("logoUpload").click()}
          >
            {organizationForm.logoPreview ? (
              <>
                <img
                  src={organizationForm.logoPreview}
                  alt="Logo preview"
                  className="img-fluid h-100 w-100"
                  style={{
                    objectFit: "cover",
                  }}
                />
                <div
                  className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                  style={{
                    backgroundColor: "rgba(0,0,0,0.3)",
                    opacity: 0,
                    transition: "opacity 0.3s ease",
                    pointerEvents: "none",
                  }}
                >
                  <i className="bi bi-pencil fs-5 text-white"></i>
                </div>
              </>
            ) : (
              <div className="h-100 w-100 d-flex flex-column align-items-center justify-content-center">
                <i className="bi bi-camera fs-4 text-muted"></i>
                <small
                  className="text-dark text-center"
                  style={{ fontSize: "0.6rem" }}
                >
                  Click to upload logo
                </small>
              </div>
            )}
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          id="logoUpload"
          accept="image/png, image/jpeg, image/jpg"
          className="d-none"
          onChange={handleLogoUpload}
        />

        {organizationForm.logoPreview && (
          <button
            type="button"
            className="btn btn-link text-danger btn-sm mt-2"
            onClick={handleRemoveLogo}
          >
            Remove
          </button>
        )}

        {/* Display image size error */}
        {imageError && (
          <small className="text-danger d-block mt-1">{imageError}</small>
        )}

        {/* Existing error handling */}
        {error.logo && (
          <small className="text-danger d-block mt-1">{error.logo}</small>
        )}
      </div>

      {/* Rest of the form remains the same */}
      <div className="mb-4">
        <label className="form-label">Name</label>
        <Form.Control
          size="lg"
          placeholder="Enter Name..."
          className="py-3"
          onFocus={() => setOnFocus({ organizationName: true })}
          onBlur={() => setOnFocus({})}
          value={organizationForm.name}
          onChange={(e) =>
            setOrganizationForm({ ...organizationForm, name: e.target.value })
          }
        />
        {error.name && <small className="text-danger">{error.name}</small>}
      </div>

      <div className="mb-4">
        <label className="form-label">Description</label>
        <Form.Control
          as="input"
          size="lg"
          placeholder="Enter Description..."
          className="py-3"
          onFocus={() => setOnFocus({ organizationDescription: true })}
          onBlur={() => setOnFocus({})}
          value={organizationForm.discription}
          onChange={(e) =>
            setOrganizationForm({
              ...organizationForm,
              discription: e.target.value,
            })
          }
        />
        {error.discription && (
          <small className="text-danger">{error.discription}</small>
        )}
      </div>

      {/* Image Cropping Modal */}
      <Modal
        show={showCropModal}
        onHide={() => setShowCropModal(false)}
        centered
        size="lg"
        className="crop-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Crop Your Logo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="crop-container p-2">
            {imageSrc && (
              <div className="d-flex flex-column align-items-center">
                <p className="text-muted mb-3">
                  Move and resize the crop area to select your logo. A square
                  crop works best.
                </p>
                <div className="crop-area-container">
                  <ReactCrop
                    crop={crop}
                    onChange={(c) => setCrop(c)}
                    onComplete={(c) => setCompletedCrop(c)}
                    aspect={1}
                    circularCrop
                    className="mx-auto d-block"
                  >
                    <img
                      ref={imgRef}
                      src={imageSrc}
                      alt="Upload"
                      onLoad={onImageLoad}
                      style={{ maxHeight: "70vh", maxWidth: "100%" }}
                    />
                  </ReactCrop>
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowCropModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={applyCrop}
            disabled={!completedCrop?.width || !completedCrop?.height}
          >
            Apply
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default OrganizationForm;
